// src/features/media/useVideos.ts

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MediaApi } from './MediaApi';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { VideoViewData } from '@/lib/types/Media';

export interface UseVideosResult {
  videos: VideoViewData[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch all videos for the current customer.
 * Uses TanStack Query for caching and automatic refetching.
 */
export function useVideos(): UseVideosResult {
  const { selectedCId } = useAuth();
  const queryKey = useMemo(
    () => MEDIA_QUERY_KEYS.videos(selectedCId ?? 0),
    [selectedCId]
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await MediaApi.getVideos(selectedCId!);
      return result.data;
    },
    enabled: selectedCId != null,
    staleTime: 2 * 60 * 1000,  // 2 minutes
    gcTime: 10 * 60 * 1000,    // 10 minutes
    refetchOnMount: 'always',
  });

  return {
    videos: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
